import { Component, ErrorInfo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AlertTriangle, Bug, Home, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@primitive/button';
import { useUIStore } from '@stores/useUIStore';
import { useAuthStore } from '@stores/useAuthStore';
import { formatError } from '@utils';
import { PageErrorBoundaryProps, PageErrorBoundaryState } from '@types';
import { FeedbackButton } from './FeedbackButton';

const CHUNK_RELOAD_STORAGE_KEY = 'bbb:last-chunk-error-reload';
const CHUNK_RELOAD_GUARD_MS = 10 * 60 * 1000;

function isStaleChunkLoadError(error: Error) {
    const message = `${error.name} ${error.message}`.toLowerCase();

    return (
        message.includes('failed to fetch dynamically imported module') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('importing a module script failed') ||
        message.includes('chunkloaderror') ||
        message.includes('loading chunk')
    );
}

function shouldReloadForStaleChunk() {
    try {
        const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) ?? 0);
        return !lastReloadAt || Date.now() - lastReloadAt > CHUNK_RELOAD_GUARD_MS;
    } catch {
        return true;
    }
}

function markStaleChunkReloadAttempt() {
    try {
        sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    } catch {
        // If session storage is unavailable, the guarded reload is still the safest recovery.
    }
}

export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
    state: PageErrorBoundaryState = {
        error: null,
    };

    private clearChunkReloadTimer: ReturnType<typeof setTimeout> | null = null;

    static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
        return { error };
    }

    componentDidMount() {
        this.clearChunkReloadTimer = setTimeout(() => {
            try {
                sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
            } catch {
                // Storage cleanup is best-effort.
            }
        }, CHUNK_RELOAD_GUARD_MS);
    }

    componentWillUnmount() {
        if (this.clearChunkReloadTimer) {
            clearTimeout(this.clearChunkReloadTimer);
        }
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Log locally so we have context during development.
        console.error('PageErrorBoundary caught an error', error, info);
        const staleChunkLoadError = isStaleChunkLoadError(error);

        if (staleChunkLoadError && shouldReloadForStaleChunk()) {
            markStaleChunkReloadAttempt();
            window.location.reload();
        }
    }

    private resetErrorBoundary = () => {
        this.setState({ error: null });
        this.props.onReset?.();
    };

    render() {
        const { error } = this.state;
        const { children, renderFallback } = this.props;

        if (error) {
            if (renderFallback) {
                return renderFallback(error, this.resetErrorBoundary);
            }
            return <DefaultErrorFallback error={error} onRetry={this.resetErrorBoundary} />;
        }

        return children;
    }
}

type DefaultErrorFallbackProps = {
    error: Error;
    onRetry: () => void;
};

function DefaultErrorFallback({ error, onRetry }: DefaultErrorFallbackProps) {
    const [isFallbackFeedbackOpen, setIsFallbackFeedbackOpen] = useState(false);
    const { setShowFeedback, setFeedbackPrefillMessage } = useUIStore(
        useShallow((s) => ({
            setShowFeedback: s.setShowFeedback,
            setFeedbackPrefillMessage: s.setFeedbackPrefillMessage,
        })),
    );
    const { isAuthenticated, logout } = useAuthStore(
        useShallow((s) => ({
            logout: s.logout,
            isAuthenticated: s.isAuthenticated,
        })),
    );

    const openFeedback = () => {
        setFeedbackPrefillMessage(formatError(error));
        setIsFallbackFeedbackOpen(true);
        setShowFeedback(true);
    };

    const handleStartClean = async () => {
        try {
            await logout();
        } catch (logoutError) {
            console.error('Failed to logout during clean reset', logoutError);
        }

        try {
            localStorage.clear();
            sessionStorage?.clear();
            document.cookie.split(';').forEach((cookie) => {
                const [name] = cookie.split('=');
                if (!name) return;
                document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            });
        } catch (storageError) {
            console.error('Failed to clear storage/cookies during clean reset', storageError);
        }

        // Full reload to rehydrate the app fresh.
        window.location.href = '/';
    };

    return (
        <div className="flex h-[70vh] flex-col items-center justify-center text-center pt-6">
            {/* Icon + title */}
            <div className="flex flex-col items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>

                <h2 className="text-lg font-semibold">Something went wrong on our side</h2>

                <p className="max-w-md text-sm text-muted-foreground">
                    We’re already looking into it.
                </p>

                {isAuthenticated &&
                    <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                        <span>You can</span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={openFeedback}
                            className="gap-1.5 bg-muted"
                        >
                            <Bug className="h-4 w-4" />
                            Help us fix it faster
                        </Button>
                    </div>
                }
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-center gap-3 border border-transparent hover:border-muted-foreground/30">
                <Button
                    size="sm"
                    onClick={() => window.location.reload()}
                    className="gap-1.5 "
                >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                </Button>

                <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        onRetry();
                        window.location.href = '/';
                    }}
                    className="gap-1.5"
                >
                    <Home className="h-4 w-4" />
                    Home
                </Button>
            </div>
            <div className='pt-8'>
                <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleStartClean}
                    className="gap-1.5"
                >
                    <Trash2 className="h-4 w-4" />
                    Start clean (log out & clear cache)
                </Button>
            </div>
            <FeedbackButton
                hideFloatingButton
                isOpen={isFallbackFeedbackOpen}
                onOpenChange={(open) => {
                    setIsFallbackFeedbackOpen(open);
                    setShowFeedback(open);
                }}
            />
        </div>
    );
}
